/**
 * Data Table Widget (Story 5.5, Task 6 → updated Story 5.7, Task 4)
 *
 * Renders tabular data with sortable columns and row limiting.
 * Uses plain HTML table with Tailwind styling.
 *
 * Column priority heuristic (Story 5.7):
 * - First column: always visible (highest priority)
 * - Numeric columns: next priority
 * - Text columns: lowest priority, hidden first as colSpan narrows
 * Horizontal scroll at narrow colSpan (≤ 4) with overscroll-behavior: contain.
 */

import { useState, useMemo, useCallback } from "react";
import { TestTube, Package, TrendUp } from "@phosphor-icons/react";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { TabularData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isTabularData } from "../../../lib/widgetData";
// Story 22.5 — Recording playback cell for CallRail / CTM calls-list widgets
import { RecordingPlayCell } from "./RecordingPlayCell";
import { useChartWidgetShell, ChartMessage } from "./chartWidgetShell";

interface DataTableWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<TabularData>;
}

type SortDirection = "asc" | "desc" | null;

/** Map Mailchimp campaign type values to user-facing labels */
const CAMPAIGN_TYPE_LABELS: Record<string, string> = {
  regular: "Newsletter",
  automation: "Automation",
  rss: "RSS",
};

function isNumeric(val: unknown): val is number {
  return typeof val === "number";
}

/**
 * Determines visible columns based on priority heuristic.
 * First column always shown, then numeric columns, then text columns.
 * Number of visible columns depends on colSpan.
 */
function getVisibleColumns(
  columns: string[],
  rows: Record<string, unknown>[],
  colSpan: number
): string[] {
  if (columns.length === 0) return columns;

  // Max columns based on colSpan
  const maxCols = colSpan <= 3 ? 2 : colSpan <= 4 ? 3 : colSpan <= 6 ? 5 : columns.length;

  // No sample row to classify by type — fall back to positional priority
  if (rows.length === 0) return columns.slice(0, Math.min(columns.length, maxCols));
  if (columns.length <= maxCols) return columns;

  // First column is always highest priority
  const firstCol = columns[0]!;
  const rest = columns.slice(1);

  // Classify remaining columns by data type from first row
  const sampleRow = rows[0] ?? {};
  const numericCols = rest.filter((col) => isNumeric(sampleRow[col]));
  const textCols = rest.filter((col) => !isNumeric(sampleRow[col]));

  // Priority: first col, then numeric, then text
  const prioritized = [firstCol, ...numericCols, ...textCols];
  return prioritized.slice(0, maxCols);
}

/**
 * Story 22.5 AC #9 — when `recordingColumn` is set on the widget config,
 * the renderer appends a final "Play" column that emits a
 * `<RecordingPlayCell>` per row. The cell is hidden for rows where
 * `_recordingsEnabled === false` OR where the recordingId field is
 * null/empty (no upstream recording asset).
 *
 * Reads `_integrationId` from the row (denormalized by the widget-data
 * pipeline) AND falls back to `config.integrationId` for fixtures.
 *
 * Reads optional `_portalToken` from the row metadata when running
 * inside the public client portal; the upstream widget-data pipeline
 * populates this for portal-view payloads only.
 */
interface RecordingColumnConfig {
  platform: "callrail" | "calltrackingmetrics";
  recordingIdField: string;
}

export function DataTableWidget({ widget, data }: DataTableWidgetProps) {
  const config = widget.config as {
    columns?: string[];
    sortBy?: string;
    limit?: number;
    recordingColumn?: RecordingColumnConfig;
    integrationId?: string;
  };
  const columns = config.columns ?? [];
  const limit = config.limit ?? 10;
  const colSpan = widget.position.colSpan;
  const useHorizontalScroll = colSpan <= 4;
  const recordingColumn = config.recordingColumn;

  const { shell } = useChartWidgetShell(widget.config);

  const [sortColumn, setSortColumn] = useState<string | null>(config.sortBy ?? null);
  const [sortDir, setSortDir] = useState<SortDirection>(config.sortBy ? "desc" : null);
  const [imgErrors, setImgErrors] = useState<Set<string>>(() => new Set());

  const handleImgError = useCallback((url: string) => {
    setImgErrors((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  const handleSort = (col: string) => {
    if (sortColumn === col) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else if (sortDir === "desc") {
        setSortColumn(null);
        setSortDir(null);
      } else {
        setSortDir("asc");
      }
    } else {
      setSortColumn(col);
      setSortDir("asc");
    }
  };

  // Loading state
  if (data?.loading) {
    return shell(
      "data-table",
      widget.title,
      <div className="flex-1 space-y-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-6 animate-pulse rounded bg-muted" />
        ))}
      </div>,
    );
  }

  // Error state
  if (data?.error) {
    return shell("data-table", widget.title, <ChartMessage role="alert">{data.error}</ChartMessage>, { centered: true });
  }

  const tableData = data?.data;

  // No data state
  if (!tableData || !isTabularData(tableData) || tableData.rows.length === 0) {
    return shell(
      "data-table",
      widget.title,
      <ChartMessage testId="data-table-no-data">No data available</ChartMessage>,
      { centered: true },
    );
  }

  // Heuristic column visibility based on colSpan
  const visibleColumns = getVisibleColumns(columns, tableData.rows, colSpan);

  // Sort and limit rows
  const sortedRows = useMemo(() => {
    const rows = [...tableData.rows];
    if (sortColumn && sortDir) {
      rows.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (isNumeric(aVal) && isNumeric(bVal)) {
          return sortDir === "asc" ? aVal - bVal : bVal - aVal;
        }
        const strA = String(aVal);
        const strB = String(bVal);
        return sortDir === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
      });
    }
    return rows.slice(0, limit);
  }, [tableData.rows, sortColumn, sortDir, limit]);

  const totalRows = tableData.totalRows ?? tableData.rows.length;

  const body = (
    <>
      <div
        className="min-w-0 flex-1 overflow-x-auto"
        style={useHorizontalScroll ? { overscrollBehavior: "contain" } : undefined}
      >
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              {visibleColumns.map((col) => {
                const isSorted = sortColumn === col;
                const indicator = isSorted
                  ? sortDir === "asc" ? " \u25B2" : " \u25BC"
                  : "";
                return (
                  <th
                    key={col}
                    scope="col"
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer select-none px-2.5 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
                    onClick={() => handleSort(col)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSort(col);
                      }
                    }}
                    data-testid={`table-header-${col}`}
                  >
                    {col}{indicator}
                  </th>
                );
              })}
              {/* Story 22.5 \u2014 Play column header */}
              {recordingColumn && (
                <th
                  scope="col"
                  className="select-none px-2.5 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
                  data-testid="table-header-recording"
                >
                  Play
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                className="border-b border-border/40 last:border-0"
                data-testid="data-table-row"
              >
                {visibleColumns.map((col, colIdx) => {
                  const val = row[col];
                  const numeric = isNumeric(val);
                  const isFirstCol = colIdx === 0;
                  const rowMeta = row as Record<string, unknown>;
                  const rowIsAbTest = rowMeta._isAbTest === true;
                  const campaignType = rowMeta._campaignType as string | undefined;
                  const displayVal = val == null || val === undefined || val === ""
                    ? "--"
                    : numeric
                      ? new Intl.NumberFormat("en-US").format(val)
                      : String(val);

                  const productImageUrl = rowMeta._productImageUrl as string | undefined;
                  const isNew = rowMeta._isNew === true;
                  const isTrending = rowMeta._isTrending === true;
                  const revenueChangePct = rowMeta._revenueChangePercent as number | null | undefined;
                  const unitsChange = rowMeta._unitsChange as number | undefined;
                  const previousUnits = rowMeta._previousUnits as number | undefined;
                  const unitsChangePct = previousUnits && previousUnits > 0 ? ((unitsChange ?? 0) / previousUnits) * 100 : null;

                  // Determine if this column should show a comparison change indicator
                  const isRevenueCol = !isFirstCol && numeric && col.toLowerCase().includes("revenue") && revenueChangePct !== null && revenueChangePct !== undefined;
                  const isUnitsCol = !isFirstCol && numeric && col.toLowerCase().includes("unit") && unitsChange !== undefined;
                  const compChangePct = isRevenueCol ? revenueChangePct : isUnitsCol ? unitsChangePct : null;

                  return (
                    <td
                      key={col}
                      className={`max-w-[200px] truncate px-2.5 py-1.5 text-sm text-foreground ${numeric ? "text-right font-mono tabular-nums" : "text-left"}`}
                      title={typeof val === "string" ? val : undefined}
                    >
                      <span className="flex items-center gap-2">
                        {isFirstCol && productImageUrl && !imgErrors.has(productImageUrl) ? (
                          <img
                            src={productImageUrl}
                            alt=""
                            className="w-8 h-8 rounded-md object-cover shrink-0"
                            loading="lazy"
                            onError={() => handleImgError(productImageUrl)}
                            data-testid="product-thumbnail"
                          />
                        ) : isFirstCol && productImageUrl !== undefined ? (
                          <Package size={20} className="text-muted-foreground shrink-0" data-testid="product-thumbnail-fallback" />
                        ) : null}
                        <span className="truncate">{displayVal}</span>
                        {isFirstCol && rowIsAbTest && (
                          <span className="inline-flex items-center gap-0.5 shrink-0 px-1 py-0.5 rounded text-[10px] text-muted-foreground bg-muted" data-testid="ab-test-badge">
                            <TestTube size={14} />
                            A/B
                          </span>
                        )}
                        {isFirstCol && campaignType && !rowIsAbTest && (
                          <span className="shrink-0 px-1 py-0.5 rounded text-[10px] text-muted-foreground bg-muted" data-testid="campaign-type-badge">
                            {CAMPAIGN_TYPE_LABELS[campaignType] ?? campaignType}
                          </span>
                        )}
                        {isFirstCol && isNew && (
                          <span className="inline-flex items-center gap-0.5 shrink-0 px-1 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700" data-testid="new-badge">New</span>
                        )}
                        {isFirstCol && isTrending && !isNew && (
                          <span className="inline-flex items-center gap-0.5 shrink-0 px-1 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700" data-testid="trending-badge"><TrendUp size={12} /> Trending</span>
                        )}
                        {compChangePct !== null && compChangePct !== undefined && (
                          <span className={`text-[10px] shrink-0 ${compChangePct > 0 ? "text-emerald-600" : compChangePct < 0 ? "text-red-500" : "text-muted-foreground"}`} data-testid={isRevenueCol ? "revenue-change" : "units-change"}>
                            ({compChangePct > 0 ? "+" : ""}{compChangePct.toFixed(1)}%)
                          </span>
                        )}
                      </span>
                    </td>
                  );
                })}
                {/* Story 22.5 — Play cell */}
                {recordingColumn && (() => {
                  const rowMeta2 = row as Record<string, unknown>;
                  const rid = rowMeta2[recordingColumn.recordingIdField];
                  const recordingsEnabled =
                    rowMeta2._recordingsEnabled !== false;
                  const portalToken =
                    typeof rowMeta2._portalToken === "string"
                      ? (rowMeta2._portalToken as string)
                      : undefined;
                  const rowIntegrationId =
                    typeof rowMeta2._integrationId === "string"
                      ? (rowMeta2._integrationId as string)
                      : (config.integrationId ?? "");

                  if (!recordingsEnabled) {
                    return (
                      <td
                        key="__recording__"
                        className="px-2.5 py-1.5 text-xs text-muted-foreground"
                        data-testid="recording-disabled-cell"
                      >
                        🔇 Recording disabled
                      </td>
                    );
                  }
                  if (rid === null || rid === undefined || rid === "") {
                    return (
                      <td
                        key="__recording__"
                        className="px-2.5 py-1.5 text-xs text-muted-foreground"
                      >
                        —
                      </td>
                    );
                  }
                  if (!rowIntegrationId) {
                    return (
                      <td
                        key="__recording__"
                        className="px-2.5 py-1.5 text-xs text-muted-foreground"
                      >
                        —
                      </td>
                    );
                  }
                  return (
                    <td
                      key="__recording__"
                      className="px-2.5 py-1.5"
                      data-testid="recording-play-cell"
                    >
                      <RecordingPlayCell
                        integrationId={rowIntegrationId}
                        recordingId={String(rid)}
                        portalToken={portalToken}
                      />
                    </td>
                  );
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalRows > limit && (
        <div className="mt-2 px-2.5 text-[11px] text-muted-foreground" data-testid="table-row-count">
          Showing {sortedRows.length} of {totalRows} rows
        </div>
      )}
    </>
  );

  return shell("data-table", widget.title, body);
}
